import {
  createFlow,
  csrfTokenOf,
  fetchFlow,
  kratosFetch,
  type KratosFlow,
  messagesOf,
  SignInCodeError,
} from '@/lib/auth/kratos-code-login'

/**
 * Social sign-in (Google / Apple / Microsoft) over Ory Kratos' `oidc` method.
 *
 * 1. Start a browser login flow whose `return_to` is our `/api/auth/kratos/callback`.
 * 2. Submit `method: oidc` as JSON — Kratos answers 422 `browser_location_change_required`
 *    with the provider's authorize URL, which we navigate to.
 * 3. The provider returns to Kratos; an unknown identity is registered on the spot
 *    (kratos.yml `registration.after.oidc` session hook), then Kratos redirects to the callback,
 *    which exchanges the Kratos session for a NextAuth/Strapi session.
 * 4. Anything that needs the user instead (the email already has an account, a provider error)
 *    lands on /login — kratos.yml login / registration / error `ui_url` — see readSocialRedirect.
 */

export const SOCIAL_PROVIDERS = ['google', 'apple', 'microsoft'] as const
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number]

const CALLBACK_PATH = '/api/auth/kratos/callback'
/** Kratos message id: the social account's email already belongs to an identity (oidc/strategy.go). */
const MSG_LOGIN_LINK = 1010016

/** Navigates away on success; throws SignInCodeError ('unavailable' | 'rejected') otherwise. */
export async function startSocialSignIn(provider: SocialProvider, returnTo?: string): Promise<void> {
  const callback = new URL(CALLBACK_PATH, window.location.origin)
  if (returnTo) callback.searchParams.set('next', returnTo)

  const flow = await createFlow('login', callback.toString())
  // Kratos only renders provider nodes for providers configured in SELFSERVICE_METHODS_OIDC_CONFIG_PROVIDERS.
  const offered = flow.ui.nodes.some(
    (node) => node.attributes.name === 'provider' && node.attributes.value === provider
  )
  if (!offered) throw new SignInCodeError('unavailable')

  const res = await kratosFetch(`/self-service/login?flow=${encodeURIComponent(flow.id)}`, {
    method: 'oidc',
    provider,
    csrf_token: csrfTokenOf(flow),
  })
  const body = (await res.json().catch(() => ({}))) as { redirect_browser_to?: string }
  if (res.status === 422 && body.redirect_browser_to) {
    window.location.assign(body.redirect_browser_to)
    return
  }
  throw new SignInCodeError(res.status >= 500 ? 'unavailable' : 'rejected')
}

export type SocialRedirect =
  /** Sign in to the existing account on `flow` (email code) and Kratos links the social account. */
  | { kind: 'link'; flow: KratosFlow; email: string; provider: string; returnTo?: string }
  | { kind: 'error' }

/** The `next` our callback URL carried, i.e. where the user was headed before the social detour. */
function nextOf(flowReturnTo?: string): string | undefined {
  if (!flowReturnTo) return undefined
  const url = new URL(flowReturnTo, window.location.origin)
  const next = url.searchParams.get('next')
  const ours = url.origin === window.location.origin && url.pathname === CALLBACK_PATH
  // Same-origin relative paths only, like the callback route — blocks `//evil.com`.
  return ours && next && /^\/(?![/\\])/.test(next) ? next : undefined
}

/**
 * What Kratos sent /login after a social sign-in that needs the user: `?flow=` (`flowId`) for a
 * login or registration flow to continue, `?id=` (`errorId`) for a Kratos error. Null when there
 * is nothing to show.
 */
export async function readSocialRedirect(
  flowId: string | null,
  errorId: string | null
): Promise<SocialRedirect | null> {
  if (errorId) return { kind: 'error' }
  if (!flowId) return null

  // The id alone doesn't say which kind of flow it is; an unknown id is "expired" for the other kind.
  const flow = await fetchFlow('login', flowId).catch((error: unknown) => {
    if (error instanceof SignInCodeError && error.reason === 'expired') return fetchFlow('registration', flowId)
    throw error
  })
  const messages = messagesOf(flow)
  const link = messages.find((message) => message.id === MSG_LOGIN_LINK)
  if (link) {
    return {
      kind: 'link',
      flow,
      email: String(link.context?.duplicateIdentifier ?? ''),
      provider: String(link.context?.provider ?? ''),
      returnTo: nextOf(flow.return_to),
    }
  }
  return messages.some((message) => message.type === 'error') ? { kind: 'error' } : null
}
